import React, {Component} from "react"
import {JsonDocument, Model} from "Model"
import {observer} from "mobx-react"
import {observable} from "mobx"
import {findErrors} from "lib/functions/findErrors"
import {DataStore} from "data/DataStore"
import styles from "./UploadDocs.module.css"
import {UploadInput} from "./upload-input"
import {getPartsByDelimiter} from "helper/Methods"

interface Props {
    model: Model
}

const rules = {}

@observer
export class UploadDocs extends Component<Props, {}> {
    @observable
    private errors: {[key: string]: string} = {}

    private json_data: JsonDocument = {}

    constructor(props) {
        super(props)

        this.onChange = this.onChange.bind(this)
        this.json_data = props.model.document.json_data

        this.onModel = this.onModel.bind(this)
        props.model.dispatcher.addListener(this.onModel)
    }

    private onModel() {
        this.json_data = this.props.model.document.json_data
        this.forceUpdate()
    }

    public async componentDidMount() {
        this.props.model.dispatcher.dispatch()
        this.isValid = this.isValid.bind(this)
        // @ts-ignore
        this.isValid.functionId = this.constructor.name
        this.props.model.addValidateFunction(this.isValid)
    }

    public async componentWillUnmount() {
        this.props.model.removeValidateFunction(this.isValid)
        this.props.model.dispatcher.removeListener(this.onModel)
    }

    private isValid(): boolean {
        console.log("Validate UploadDocs")
        const {isValid, errors} = findErrors(this.json_data, rules)
        this.errors = errors

        return isValid
    }

    private get dataStore(): DataStore {
        return this.props.model.dataStore
    }

    private onChange(id: string, value) {
        if (this.json_data.isSubmitted) {
            delete this.json_data.isSubmitted
        }
        this.json_data[id] = value
        this.forceUpdate()
    }

    private isManhattanCampus(): boolean {
        const model = this.props?.model
        if (!model?.campusesInfo?.length || !model?.document?.json_data?.extra) {
            return false
        }
        const [, , , manhattanCampus] = model.campusesInfo
        const {id: manhattanCampusId} = manhattanCampus
        const [campusId] = getPartsByDelimiter(model.document.json_data.campus)
        return manhattanCampusId === +campusId
    }

    public render() {
        const {model} = this.props
        const uploads = this.props.model.document.json_data.uploads || []
        const isManhattan = this.isManhattanCampus()

        const totalSlots = isManhattan ? 5 : 4
        const requiredSlotKeys = ["issuedId", "diplomaId", "militaryFrontId", "militaryBackId"]
        if (isManhattan) {
            requiredSlotKeys.push("ssnId")
        }
        const uploadedCount = requiredSlotKeys.filter((key) => uploads.some((u) => u.type === key))
            .length

        return (
            <div className={styles.root}>
                {/* Header Card */}
                <div className={styles.headerCard}>
                    <div className={styles.accentBar} />
                    <div className={styles.headerContent}>
                        <h1 className={styles.h1}>Upload Supporting Documents</h1>
                        <div className={styles.instructionBanner}>
                            <p className={styles.instructionText}>
                                <span className={styles.instructionLead}>To upload documents,</span>{" "}
                                you can scan them and save the files onto your computer{" "}
                                <span className={styles.instructionHighlight}>OR</span> take a clear
                                picture using your smartphone's camera.{" "}
                                <span className={styles.instructionSub}>
                                    Please attempt to save them under your complete name.
                                </span>
                            </p>
                        </div>
                    </div>
                </div>

                {/* Guidelines Collapsible Drawer */}
                <details className={styles.tipsDetails}>
                    <summary className={styles.tipsSummary}>
                        <div className={styles.tipsSummaryTitle}>
                            <svg
                                className={styles.tipsIcon}
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round">
                                <path d="M9 18h6" />
                                <path d="M10 22h4" />
                                <path d="M15.09 14c.18-.98.65-1.74 1.41-2.5A4.65 4.65 0 0 0 18 8 6 6 0 0 0 6 8c0 1 .23 2.23 1.5 3.5A4.61 4.61 0 0 1 8.91 14" />
                            </svg>
                            <span>Tips for high-quality phone scans & camera uploads</span>
                        </div>
                        <div className={styles.tipsSummaryAction}>
                            <span className={styles.tipsSummaryLabel}>Click to expand</span>
                            <svg
                                className={styles.tipsChevron}
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round">
                                <polyline points="6 9 12 15 18 9" />
                            </svg>
                        </div>
                    </summary>
                    <div className={styles.tipsContent}>
                        <div className={styles.tipItem}>
                            <div className={styles.tipItemIcon}>
                                <svg
                                    width="16"
                                    height="16"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round">
                                    <path d="M6 2v14a2 2 0 0 0 2 2h14" />
                                    <path d="M18 22V8a2 2 0 0 0-2-2H2" />
                                </svg>
                            </div>
                            <div>
                                <span className={styles.tipTitle}>All 4 Corners Visible</span>
                                <span className={styles.tipDesc}>
                                    Lay document flat on a contrasting dark background.
                                </span>
                            </div>
                        </div>
                        <div className={styles.tipItem}>
                            <div className={styles.tipItemIcon}>
                                <svg
                                    width="16"
                                    height="16"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round">
                                    <circle cx="12" cy="12" r="5" />
                                    <line x1="12" y1="1" x2="12" y2="3" />
                                    <line x1="12" y1="21" x2="12" y2="23" />
                                    <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
                                    <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
                                    <line x1="1" y1="12" x2="3" y2="12" />
                                    <line x1="21" y1="12" x2="23" y2="12" />
                                    <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
                                    <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
                                </svg>
                            </div>
                            <div>
                                <span className={styles.tipTitle}>Natural Lighting</span>
                                <span className={styles.tipDesc}>
                                    Avoid harsh camera flash or lamp glare obscuring text.
                                </span>
                            </div>
                        </div>
                        <div className={styles.tipItem}>
                            <div className={styles.tipItemIcon}>
                                <svg
                                    width="16"
                                    height="16"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round">
                                    <rect x="3" y="4" width="18" height="16" rx="2" />
                                    <circle cx="9" cy="10" r="2" />
                                    <line x1="15" y1="8" x2="17" y2="8" />
                                    <line x1="15" y1="12" x2="17" y2="12" />
                                    <line x1="7" y1="16" x2="17" y2="16" />
                                </svg>
                            </div>
                            <div>
                                <span className={styles.tipTitle}>Filename Guideline</span>
                                <span className={styles.tipDesc}>
                                    Save files as <em>FirstName_LastName_DocType</em> if possible.
                                </span>
                            </div>
                        </div>
                    </div>
                </details>

                {/* Required Document Slots Card */}
                <div className={styles.slotsCard}>
                    <div className={styles.slotsCardHeader}>
                        <div>
                            <h2 className={styles.slotsCardTitle}>Required Document Slots</h2>
                            <p className={styles.slotsCardSubtitle}>
                                {uploadedCount} of {totalSlots} documents uploaded. You may attach
                                or replace files in each slot below.
                            </p>
                        </div>
                    </div>

                    <div className={styles.slotsList}>
                        <UploadInput
                            model={model}
                            title="State Issued Id"
                            btnId="issuedId"
                            uploads={uploads}
                            iconType="id"
                            description="Front side showing full name, birth date, photo, and expiration."
                        />
                        <UploadInput
                            model={model}
                            title="Diploma or Transcript"
                            btnId="diplomaId"
                            uploads={uploads}
                            iconType="school"
                            description="High school diploma, official transcript, or GED equivalent."
                        />
                        <UploadInput
                            model={model}
                            title="License/Military Documentation Front"
                            btnId="militaryFrontId"
                            uploads={uploads}
                            iconType="military"
                            description="Prior beauty license, military ID front, or DD214 documentation."
                        />
                        <UploadInput
                            model={model}
                            title="License/Military Documentation Back"
                            btnId="militaryBackId"
                            uploads={uploads}
                            iconType="flip"
                            description="Reverse side showing barcode, signature strip, and security seals."
                        />
                        {isManhattan && (
                            <UploadInput
                                model={model}
                                title="Social Security Card for Kansas"
                                btnId="ssnId"
                                uploads={uploads}
                                iconType="ssn"
                                description="Required for State of Kansas Board licensing & Federal Title IV verification."
                            />
                        )}
                    </div>
                </div>
            </div>
        )
    }
}
